import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import PersonIcon from '@mui/icons-material/Person';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TableChartIcon from '@mui/icons-material/TableChart';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Axios from 'axios';
import { getAuthHeader } from '../../utils/authToken';
import dayjs from 'dayjs';

const AllCompetitionsUsageModal = ({
    open,
    onClose,
    newsletter
}) => {
    const [loading, setLoading] = useState(false);
    const [usageData, setUsageData] = useState({});
    const [competitions, setCompetitions] = useState([]);
    const [error, setError] = useState(null);

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    useEffect(() => {
        if (open && newsletter?.uuid) {
            fetchData();
        }
    }, [open, newsletter?.uuid]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            // Fetch newsletter details to get competitions
            const newsletterResponse = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${newsletter.uuid}`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setCompetitions(newsletterResponse.data.content_data?.competitions || []);

            // Fetch usage data
            const usageResponse = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${newsletter.uuid}/competition-usage`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setUsageData(usageResponse.data.usage || {});
        } catch (err) {
            console.error('Failed to fetch data:', err);
            setError(err.response?.data?.error || 'Failed to load competition usage data');
        } finally {
            setLoading(false);
        }
    };

    // Get source badge configuration
    const getSourceConfig = (comp) => {
        const usage = usageData[comp.competition_id];

        if (comp.ai_recommended && !comp.competition_id) {
            return {
                icon: <AutoAwesomeIcon fontSize="small" />,
                label: 'AI Recommended',
                color: 'info'
            };
        }

        switch (usage?.source) {
            case 'ai_newsletter':
                return {
                    icon: <AutoAwesomeIcon fontSize="small" />,
                    label: 'AI Generated',
                    color: 'info'
                };
            case 'excel_import':
                return {
                    icon: <TableChartIcon fontSize="small" />,
                    label: 'Excel Import',
                    color: 'secondary'
                };
            case 'manual':
            default:
                return {
                    icon: <PersonIcon fontSize="small" />,
                    label: 'Database',
                    color: 'default'
                };
        }
    };

    // Get exclusion status configuration
    const getExclusionConfig = (comp) => {
        const usage = usageData[comp.competition_id];

        if (!comp.competition_id) {
            return {
                icon: <AutoAwesomeIcon fontSize="small" />,
                label: 'Unsaved',
                color: 'info',
                tooltip: 'AI recommended - not saved to database yet'
            };
        }

        if (!usage?.used_at) {
            return {
                icon: <CheckCircleOutlineIcon fontSize="small" />,
                label: 'New',
                color: 'success',
                tooltip: 'Never used in a published newsletter'
            };
        }

        if (usage?.is_within_exclusion) {
            return {
                icon: <BlockIcon fontSize="small" />,
                label: `Excluded (${usage.days_until_reusable}d)`,
                color: 'warning',
                tooltip: `Within 4-month exclusion. Available again: ${dayjs(usage.exclusion_expires_at).format('MMM D, YYYY')}`
            };
        }

        return {
            icon: <CheckCircleOutlineIcon fontSize="small" />,
            label: 'Available',
            color: 'success',
            tooltip: 'Available for use in newsletters'
        };
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HistoryIcon />
                    Competition Usage - {newsletter?.title || 'Newsletter'}
                </Box>
            </DialogTitle>

            <DialogContent>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                ) : competitions.length === 0 ? (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography color="text.secondary">No competitions found in this newsletter</Typography>
                    </Box>
                ) : (
                    <Box>
                        {/* Summary Table */}
                        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>#</TableCell>
                                        <TableCell>Competition</TableCell>
                                        <TableCell>Source</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Last Used</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {competitions.map((comp, index) => {
                                        const sourceConfig = getSourceConfig(comp);
                                        const exclusionConfig = getExclusionConfig(comp);
                                        const usage = usageData[comp.competition_id];

                                        return (
                                            <TableRow key={index}>
                                                <TableCell>{index + 1}</TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                                                        {comp.title || 'Untitled'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        icon={sourceConfig.icon}
                                                        label={sourceConfig.label}
                                                        size="small"
                                                        color={sourceConfig.color}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Tooltip title={exclusionConfig.tooltip}>
                                                        <Chip
                                                            icon={exclusionConfig.icon}
                                                            label={exclusionConfig.label}
                                                            size="small"
                                                            color={exclusionConfig.color}
                                                        />
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell>
                                                    {usage?.used_at ? (
                                                        dayjs(usage.used_at).format('MMM D, YYYY')
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            Never
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Detailed History Accordions */}
                        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <HistoryIcon fontSize="small" />
                            Detailed Newsletter History
                        </Typography>

                        {competitions.map((comp, index) => {
                            const usage = usageData[comp.competition_id];
                            const history = usage?.newsletter_history || [];

                            if (!comp.competition_id) {
                                return null; // Skip AI recommended that aren't saved
                            }

                            return (
                                <Accordion key={index} sx={{ mb: 1 }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                            <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                                {index + 1}. {comp.title || 'Untitled'}
                                            </Typography>
                                            <Chip
                                                label={`${history.length} newsletter${history.length !== 1 ? 's' : ''}`}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {history.length > 0 ? (
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Month/Year</TableCell>
                                                        <TableCell>Title</TableCell>
                                                        <TableCell>Sent</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {history.map((nl, idx) => (
                                                        <TableRow key={idx}>
                                                            <TableCell>
                                                                {monthNames[nl.month - 1]} {nl.year}
                                                            </TableCell>
                                                            <TableCell>{nl.title}</TableCell>
                                                            <TableCell>
                                                                {nl.sent_at ? dayjs(nl.sent_at).format('MMM D, YYYY') : 'Not sent'}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                This competition hasn't been used in any other published newsletters.
                                            </Typography>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} variant="contained">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AllCompetitionsUsageModal;
